export type WhatsAppTemplatePresetKey =
  | "confirmation"
  | "reminder"
  | "fallback"
  | "cart_recovery"
  | "order_confirmed"
  | "dropi_guia_generada"
  | "dropi_en_camino"
  | "dropi_entregado"
  | "dropi_devuelto"
  | "dropi_cancelado";

export type WhatsAppTemplateComponent = {
  type?: string;
  text?: string;
  format?: string;
  buttons?: Array<{ type?: string; text?: string | null }>;
};

export interface WhatsAppTemplatePresetSpec {
  displayName: string;
  variables: number;
  buttons: boolean;
  headerFormat?: "IMAGE";
  suggestedName: string;
  category: "UTILITY" | "MARKETING" | "AUTHENTICATION";
  variableLabels: string[];
  recommendedBody: string;
}

export const WHATSAPP_TEMPLATE_PRESET_KEYS: WhatsAppTemplatePresetKey[] = [
  "confirmation",
  "reminder",
  "fallback",
  "cart_recovery",
  "order_confirmed",
  "dropi_guia_generada",
  "dropi_en_camino",
  "dropi_entregado",
  "dropi_devuelto",
  "dropi_cancelado",
];

export const WHATSAPP_TEMPLATE_PRESETS: Record<WhatsAppTemplatePresetKey, WhatsAppTemplatePresetSpec> = {
  confirmation: {
    displayName: "Confirmación con imagen",
    variables: 4,
    buttons: true,
    headerFormat: "IMAGE",
    suggestedName: "confirmacion_con_imagen_v2",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Productos del pedido", "Total", "Dirección completa"],
    recommendedBody:
      "Hola, {{1}}\n\n" +
      "Queremos confirmar los detalles de tu pedido y la dirección en donde será entregado:\n" +
      "✔ Producto: {{2}}\n" +
      "✔ Valor: ${{3}}\n" +
      "📍 Dirección: {{4}}\n\n" +
      "Si toda la información es correcta, pulsa en Confirmar Pedido para continuar con el envío\n" +
      "👇",
  },
  reminder: {
    displayName: "Recordatorio de pedido",
    variables: 4,
    buttons: false,
    suggestedName: "order_recordatorio_fluxi",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Productos", "Total"],
    recommendedBody:
      "Hola {{1}}, te recordamos tu pedido #{{2}}:\n\n" +
      "📦 Productos: {{3}}\n" +
      "💰 Total: ${{4}}\n\n" +
      "¿Podrías confirmar que los datos son correctos?",
  },
  fallback: {
    displayName: "Solicitud de dirección",
    variables: 5,
    buttons: false,
    suggestedName: "order_direccion_fluxi",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Producto", "Cantidad (unidades)", "Total"],
    recommendedBody:
      "Hola {{1}}, recibimos tu pedido #{{2}}:\n\n" +
      "📦 Producto: {{3}}\n" +
      "📦 Unidades: {{4}}\n" +
      "💰 Total: ${{5}}\n\n" +
      "Nos falta tu dirección de envío. ¿Podrías compartirla? Envíanos: dirección, ciudad y departamento.",
  },
  cart_recovery: {
    displayName: "Recuperación de carrito",
    variables: 4,
    buttons: false,
    suggestedName: "cart_recovery_fluxi",
    category: "MARKETING",
    variableLabels: ["Nombre del cliente", "Producto", "Cantidad (unidades)", "Total"],
    recommendedBody:
      "Hola {{1}}, vimos que dejaste tu compra incompleta 🛒\n\n" +
      "📦 Producto: {{2}}\n" +
      "📦 Unidades: {{3}}\n" +
      "💰 Total: ${{4}}\n\n" +
      "¿Quieres que te ayudemos a finalizarla? Solo respóndenos por aquí.",
  },
  order_confirmed: {
    displayName: "Pedido confirmado",
    variables: 0,
    buttons: false,
    suggestedName: "pedido_confirmado_default",
    category: "UTILITY",
    variableLabels: [],
    recommendedBody:
      "🎉 ¡Tu pedido ya está confirmado!\n" +
      "Estamos seguros de que lo vas a disfrutar muchísimo.\n" +
      "📦 Llegará a tu puerta en un lapso de 3 a 5 días hábiles.\n\n" +
      "¡Gracias por confiar en nosotros! 💚",
  },
  dropi_guia_generada: {
    displayName: "Dropi: guía generada",
    variables: 3,
    buttons: false,
    suggestedName: "dropi_guia_generada",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Número de guía"],
    recommendedBody:
      "Hola {{1}}, tu pedido #{{2}} ya fue procesado y está listo para ser enviado.\n\n" +
      "📦 Guía de envío: {{3}}\n\n" +
      "Te avisaremos cuando esté en camino.",
  },
  dropi_en_camino: {
    displayName: "Dropi: pedido en camino",
    variables: 3,
    buttons: false,
    suggestedName: "dropi_en_camino",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Número de guía"],
    recommendedBody:
      "Hola {{1}}, tu pedido #{{2}} ya está en camino 🚚\n\n" +
      "📦 Guía: {{3}}\n\n" +
      "Por favor ten el dinero listo para pagar contra entrega.",
  },
  dropi_entregado: {
    displayName: "Dropi: pedido entregado",
    variables: 3,
    buttons: false,
    suggestedName: "dropi_entregado",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Mensaje"],
    recommendedBody:
      "¡Listo {{1}}! Tu pedido #{{2}} fue entregado. 🎉\n\n" +
      "{{3}}\n\n" +
      "¡Esperamos que lo disfrutes!",
  },
  dropi_devuelto: {
    displayName: "Dropi: pedido devuelto",
    variables: 3,
    buttons: false,
    suggestedName: "dropi_devuelto",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Motivo"],
    recommendedBody:
      "Hola {{1}}, hubo un problema con la entrega de tu pedido #{{2}}.\n\n" +
      "{{3}}\n\n" +
      "Te contactaremos pronto para resolverlo.",
  },
  dropi_cancelado: {
    displayName: "Dropi: pedido cancelado",
    variables: 3,
    buttons: false,
    suggestedName: "dropi_cancelado",
    category: "UTILITY",
    variableLabels: ["Nombre del cliente", "Número de pedido", "Motivo"],
    recommendedBody:
      "Hola {{1}}, tu pedido #{{2}} ha sido cancelado.\n\n" +
      "{{3}}\n\n" +
      "Si necesitas ayuda, escríbenos.",
  },
};

export const CORE_WHATSAPP_TEMPLATE_PRESET_KEYS: WhatsAppTemplatePresetKey[] = [
  "confirmation",
  "reminder",
  "fallback",
  "cart_recovery",
  "order_confirmed",
];

export const buildWhatsAppTemplateComponents = (
  body: string,
  withButtons: boolean,
  headerFormat?: "IMAGE"
): WhatsAppTemplateComponent[] => {
  const components: WhatsAppTemplateComponent[] = [];
  if (headerFormat === "IMAGE") {
    components.push({ type: "HEADER", format: "IMAGE" });
  }
  components.push({ type: "BODY", text: body });
  if (withButtons) {
    components.push({
      type: "BUTTONS",
      buttons: [
        { type: "QUICK_REPLY", text: "Confirmar Pedido" },
        { type: "QUICK_REPLY", text: "Modificar Datos" },
      ],
    });
  }
  return components;
};

export const listMissingWhatsAppTemplatePresets = (
  templates: Array<{ name?: string | null; language?: string | null }>,
  presetKeys: WhatsAppTemplatePresetKey[] = CORE_WHATSAPP_TEMPLATE_PRESET_KEYS
) =>
  presetKeys.filter((key) => {
    const spec = WHATSAPP_TEMPLATE_PRESETS[key];
    return !templates.some((template) => template.name === spec.suggestedName && (template.language || "es") === "es");
  });
